import { useState } from 'react';
import type { KeyedMutator } from 'swr';
import type { IMfaStatusView } from '@shared/services';
import { disableMfa } from '@/shared/api/mfa';
import { getApiErrorString } from '@/shared/utils';

/**
 * Confirm-and-disable flow for the enrolled code factor — authenticator (TOTP) or email, whichever
 * the account holds — mirroring the passkey-removal shape: open a confirmation, call the
 * session-authorized disable endpoint, revalidate the shared `useMfaStatus` cache via `mutate` on
 * success so the card flips without a full reload. On failure the confirmation stays open with the
 * error so the user can retry or cancel.
 */
export const useCodeFactorDisable = (mutate: KeyedMutator<IMfaStatusView>) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = () => {
    setError(null);
    setIsConfirming(true);
  };

  const close = () => {
    setIsConfirming(false);
    setError(null);
  };

  /** Returns whether the disable succeeded, so the caller can keep the confirm dialog open on failure. */
  const disable = async (): Promise<boolean> => {
    setError(null);
    setIsDisabling(true);

    try {
      await disableMfa();
      await mutate();
      setIsConfirming(false);

      return true;
    } catch (caught) {
      // Keep the confirmation open on failure — the factor is still enabled server-side.
      setError(getApiErrorString(caught));

      return false;
    } finally {
      setIsDisabling(false);
    }
  };

  return { isConfirming, open, close, disable, isDisabling, error };
};
